import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import settings from '../config/settings.json';

export type AllFlagKeys = string;

type FlagOverrides = Partial<Record<AllFlagKeys, boolean>>;

interface FeatureFlagOverrideContextValue {
  /** What each overridden flag resolves to: the signed-in user's automatic flags, under any session override. */
  overrides: FlagOverrides;
  /** Just the automatic part (admin flags, rollout cohort, settings.json email lists), for display. */
  autoOverrides: FlagOverrides;
  /** False while the signed-in user's automatic flags are still loading (they arrive after sign-in). */
  ready: boolean;
  setOverride: (flag: AllFlagKeys, value: boolean | undefined) => void;
}

const FeatureFlagOverrideContext = createContext<FeatureFlagOverrideContextValue>({
  overrides: {},
  autoOverrides: {},
  ready: true,
  setOverride: () => {},
});

const STORAGE_KEY = 'bhcs_flag_overrides';

function loadOverrides(): FlagOverrides {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FlagOverrides) : {};
  } catch {
    return {};
  }
}

/** The settings.json keys that turn flags on or off per user. */
export interface FlagTargeting {
  /** Flag → emails forced ON (opt-in before GA). */
  experimentalUsers?: Record<string, unknown>;
  /** Flag → emails forced OFF (opt-out after GA); wins over everything automatic. */
  flagExceptions?: Record<string, unknown>;
  /** Flag → true: ON for every admin (`is_admin()`), ahead of any parent rollout. */
  adminFlags?: Record<string, unknown>;
}

export interface FlagLookups {
  isAdmin: () => Promise<boolean>;
  /** The flags `feature_flag_cohorts` opts this parent into (`my_flag_cohorts()`). */
  cohortFlags: () => Promise<string[]>;
}

/**
 * The flags a signed-in user gets without touching the override panel. ON: their email in
 * `experimentalUsers`, being an admin for an `adminFlags` flag, or a row in the flag's rollout
 * cohort. OFF: their email in `flagExceptions`, applied last so it wins. A lookup that fails
 * leaves its flags at their default rather than guessing.
 */
export async function resolveAutoFlags(email: string, cfg: FlagTargeting, lookups: FlagLookups): Promise<FlagOverrides> {
  const auto: FlagOverrides = {};
  // Array.isArray / === true skip the _comment strings.
  for (const [flag, emails] of Object.entries(cfg.experimentalUsers ?? {})) {
    if (Array.isArray(emails) && emails.includes(email)) auto[flag] = true;
  }
  const adminFlags = Object.entries(cfg.adminFlags ?? {}).filter(([, on]) => on === true).map(([flag]) => flag);
  const [isAdmin, cohort] = await Promise.all([
    adminFlags.length > 0 ? lookups.isAdmin().catch(() => false) : Promise.resolve(false),
    lookups.cohortFlags().catch(() => [] as string[]),
  ]);
  if (isAdmin) for (const flag of adminFlags) auto[flag] = true;
  for (const flag of cohort) auto[flag] = true;
  for (const [flag, emails] of Object.entries(cfg.flagExceptions ?? {})) {
    if (Array.isArray(emails) && emails.includes(email)) auto[flag] = false;
  }
  return auto;
}

const supabaseLookups: FlagLookups = {
  isAdmin: async () => {
    const { data, error } = await supabase.rpc('is_admin');
    if (error) throw error;
    return data === true;
  },
  cohortFlags: async () => {
    const { data, error } = await supabase.rpc('my_flag_cohorts');
    if (error) throw error;
    return Array.isArray(data) ? data.filter((f): f is string => typeof f === 'string') : [];
  },
};

export function FeatureFlagOverrideProvider({ children }: { children: ReactNode }) {
  // Explicit session choices from the override panel (persisted for the tab).
  const [manual, setManual] = useState<FlagOverrides>(loadOverrides);
  // What the signed-in user gets automatically; recomputed whenever the user changes.
  const [auto, setAuto] = useState<FlagOverrides>({});
  // Until they load, a flag reads its settings.json default. A page that redirects when its flag
  // is off waits for this, or a cohort parent who reloads it is sent away.
  const [ready, setReady] = useState(() => !supabase.auth?.getSession);

  useEffect(() => {
    const auth = supabase.auth;
    if (!auth?.getSession) return;
    let live = true;
    let resolvedFor: string | null | undefined;

    const resolveFor = (email: string | null) => {
      if (email === resolvedFor) return;
      resolvedFor = email;
      if (!email) {
        setAuto({});
        setReady(true);
        return;
      }
      setReady(false);
      void resolveAutoFlags(email, settings as FlagTargeting, supabaseLookups).then((next) => {
        if (live && resolvedFor === email) {
          setAuto(next);
          setReady(true);
        }
      });
    };

    auth.getSession().then(({ data: { session } }) => resolveFor(session?.user?.email ?? null));
    // A sign-in that completes after this mounts (the OAuth return) must still pick up the user's
    // flags. Deferred: Supabase deadlocks if its client is awaited inside this callback.
    const subscription = typeof auth.onAuthStateChange === 'function'
      ? auth.onAuthStateChange((_event, session) => {
          const email = session?.user?.email ?? null;
          setTimeout(() => resolveFor(email), 0);
        })?.data?.subscription
      : undefined;
    return () => {
      live = false;
      subscription?.unsubscribe();
    };
  }, []);

  const setOverride = (flag: AllFlagKeys, value: boolean | undefined) => {
    setManual(prev => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[flag];
      } else {
        next[flag] = value;
      }
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const overrides = useMemo(() => ({ ...auto, ...manual }), [auto, manual]);

  return (
    <FeatureFlagOverrideContext.Provider value={{ overrides, autoOverrides: auto, ready, setOverride }}>
      {children}
    </FeatureFlagOverrideContext.Provider>
  );
}

export function useFeatureFlagOverrides() {
  return useContext(FeatureFlagOverrideContext);
}

/** Whether the signed-in user's automatic flags (admin, cohort) have loaded. */
export function useFlagsReady(): boolean {
  return useContext(FeatureFlagOverrideContext).ready;
}

/**
 * Resolves a single boolean feature flag key, applying any session override on
 * top of the canonical value from settings.json.
 *
 * Usage: const enabled = useFlag('parentPickupDetails', settings.parentPickupDetails)
 */
export function useFlag(key: string, canonicalValue: boolean): boolean {
  const { overrides } = useContext(FeatureFlagOverrideContext);
  return key in overrides ? (overrides[key] as boolean) : canonicalValue;
}
